import { ChangeEvent, useMemo } from 'react';

import { Download } from '@mui/icons-material';
import { Divider, Stack, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectAnalysisName,
  selectIncludeMtr,
  selectMatrixVisualize,
  setMatrixVisualize,
  useLazyGetMtrGeojsonQuery,
} from 'state/originDestinationSlices';
import {
  selectCurrentAnalysisId,
  selectProject,
  setMaximize,
} from 'state/workflowSlice';

import { MAXIMIZE_MAP } from 'appConstants';
import { StlButton } from 'features/common/StlButton/StlButton';
import { StlSwitchWithLabel } from 'features/common/StlSwitch/StlSwitch';
import { triggerGeojsonExport } from 'features/common/exportUtils';
import { showToast } from 'features/common/toasts/ToastProvider';

import styles from '../../task_bar/Taskbar.module.css';

enum DownloadType {
  OD = 'OD Data',
  MTR = 'MTR Data',
}

export function ODMetricsDownload() {
  const dispatch = useAppDispatch();
  const visualize = useAppSelector(selectMatrixVisualize);
  const project = useAppSelector(selectProject);
  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const analysisName = useAppSelector(selectAnalysisName);
  const includeMtr = useAppSelector(selectIncludeMtr);
  const [fetchMtrGeojson] = useLazyGetMtrGeojsonQuery();

  const downloadOptions: DownloadType[] = useMemo(() => {
    if (includeMtr) return Object.values(DownloadType);
    return [DownloadType.OD];
  }, [includeMtr]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const showMatrix = event.target.checked;
    dispatch(setMatrixVisualize(showMatrix));
    dispatch(setMaximize(showMatrix ? null : MAXIMIZE_MAP));
  };

  const handleDownload = async (type: DownloadType) => {
    if (!project || !currentAnalysisId) return;

    if (type === DownloadType.OD) {
      const url = `/server/od_analysis/${project}/${currentAnalysisId}/download.csv/`;
      window.open(url, '_blank', 'noopener,noreferrer')?.focus();
      return;
    }

    try {
      const geojson = await fetchMtrGeojson({
        project_slug: project,
        analysis_id: currentAnalysisId,
      }).unwrap();
      triggerGeojsonExport(`${analysisName}_mtr.geojson`, geojson);
    } catch (error) {
      showToast('error', 'Failed to download MTR geojson');
      console.error('Failed to download MTR geojson', error);
    }
  };

  return (
    <div className={styles.task_bar_component}>
      <Divider />
      <Stack display="flex">
        <StlSwitchWithLabel
          label="OD Matrix"
          checked={visualize}
          onChange={handleChange}
        />
        <Divider />
        <Typography lineHeight="40px" pl={0.5} variant="h4">
          Downloads
        </Typography>
        <Stack gap={0.5}>
          {downloadOptions.map((type) => (
            <StlButton
              isActive
              key={type}
              value={type}
              handleClick={() => handleDownload(type)}
              classes={[styles.btn_32]}
              icon={<Download />}
            />
          ))}
        </Stack>
      </Stack>
    </div>
  );
}
